import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { AutoComplete } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import navigationConfig from "../../config/navigationConfig.json";

const GlobalSearch = () => {
  const [searchValue, setSearchValue] = useState("");
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const allPages = useMemo(() => {
    const pages = [];
    navigationConfig.sections.forEach((section) => {
      section.items.forEach((item) => {
        pages.push({
          label: item.label,
          path: item.path,
          section: section.title,
        });
      });
    });
    return pages;
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) return [];

    const query = searchValue.toLowerCase();
    const matched = allPages.filter(
      (page) =>
        page.label.toLowerCase().includes(query) ||
        page.section.toLowerCase().includes(query)
    );

    const grouped = {};
    matched.forEach((page) => {
      if (!grouped[page.section]) grouped[page.section] = [];
      grouped[page.section].push(page);
    });

    return Object.entries(grouped).map(([section, pages]) => ({
      label: <span className="global-search-group">{section}</span>,
      options: pages.map((page) => ({
        value: page.path,
        label: (
          <div className="global-search-item">
            <span className="global-search-item-label">{page.label}</span>
            <span className="global-search-item-section">{page.section}</span>
          </div>
        ),
      })),
    }));
  }, [searchValue, allPages]);

  const handleSelect = useCallback(
    (path) => {
      navigate(path);
      setSearchValue("");
      setExpanded(false);
      inputRef.current?.blur();
    },
    [navigate]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === "/" &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(
          document.activeElement?.tagName
        ) &&
        !document.activeElement?.isContentEditable
      ) {
        e.preventDefault();
        setExpanded(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape" && expanded) {
        setExpanded(false);
        setSearchValue("");
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [expanded]);

  const handleMobileToggle = () => {
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setExpanded(false);
      setSearchValue("");
    }, 200);
  };

  return (
    <>
      <div className={`global-search-wrapper ${expanded ? "expanded" : ""}`}>
        <div className="global-search-box">
          <SearchOutlined className="global-search-icon" />
          <AutoComplete
            ref={inputRef}
            className="global-search-autocomplete"
            options={filteredOptions}
            onSelect={handleSelect}
            value={searchValue}
            onChange={setSearchValue}
            onBlur={handleBlur}
            popupMatchSelectWidth={360}
            placeholder="Search pages..."
          />
          <kbd className="global-search-shortcut">/</kbd>
        </div>
      </div>
      <button
        className="global-search-mobile-btn"
        onClick={handleMobileToggle}
        aria-label="Search"
      >
        <SearchOutlined />
      </button>
    </>
  );
};

export default GlobalSearch;
